import React from 'react';
import { ChevronRight } from 'lucide-react';
import { TIER_COLORS, STATUS_LABELS } from '@/lib/format';

export const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-surface border border-line rounded-2xl ${className}`} {...props}>
    {children}
  </div>
);

export const SectionLabel = ({ children, className = '' }) => (
  <p className={`mini-label mb-2 px-1 ${className}`}>{children}</p>
);

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="font-serif text-lg text-ink">{children}</h2>
    {action}
  </div>
);

export const Button = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 bg-ink text-ground rounded-xl px-4 py-3 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const GhostButton = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 bg-transparent border border-line text-ink rounded-xl px-4 py-3 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const PILL_TONES = {
  gold: 'text-gold border-gold/40',
  red: 'text-redx border-redx/40',
  green: 'text-pos border-pos/40',
  neutral: 'text-ink-soft border-line',
};
const PILL_DOTS = { gold: 'bg-gold', red: 'bg-redx', green: 'bg-pos', neutral: 'bg-ink-faint' };

export const Pill = ({ children, tone = 'gold', className = '', ...props }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${PILL_TONES[tone] || PILL_TONES.gold} ${className}`}
    {...props}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${PILL_DOTS[tone] || PILL_DOTS.gold}`} />
    {children}
  </span>
);

export const StatusBadge = ({ status }) => {
  const toneMap = { pending: 'gold', confirmed: 'neutral', approved: 'neutral', paid: 'green', reversed: 'red', recorded: 'neutral', returned: 'red', active: 'green', passive: 'red' };
  const label = STATUS_LABELS[status] || (status === 'active' ? 'Aktif' : status === 'passive' ? 'Pasif' : status);
  return <Pill tone={toneMap[status] || 'neutral'}>{label}</Pill>;
};

export const TierBadge = ({ tier }) => {
  const color = TIER_COLORS[tier] || '#A9793D';
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full font-semibold border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em]"
      style={{ color, borderColor: color + '66' }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {tier}
    </span>
  );
};

// Circular marker holding a serif single letter (tier ladder)
export const TierMarker = ({ tier, active }) => {
  const color = TIER_COLORS[tier] || '#A9793D';
  const letter = (tier || '?').charAt(0).toUpperCase();
  return (
    <span
      className="w-9 h-9 rounded-full flex items-center justify-center font-serif text-base shrink-0 border"
      style={active
        ? { backgroundColor: color + '22', color, borderColor: color }
        : { color: 'rgb(var(--ink-soft))', borderColor: 'rgb(var(--line))' }}
    >
      {letter}
    </span>
  );
};

// 3px progress bar, hair track, gold fill
export const Progress = ({ value = 0, className = '' }) => (
  <div className={`h-[3px] bg-hair rounded-full overflow-hidden ${className}`}>
    <div className="h-full rounded-full bg-gold transition-all duration-500" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
  </div>
);

// Big hero stat: mini label above, large serif tabular number, optional delta below
export const StatHero = ({ label, value, delta, deltaTone = 'green', valueClass = 'text-ink', testid }) => (
  <div>
    <p className="mini-label">{label}</p>
    <p className={`font-serif text-5xl tabular mt-1 ${valueClass}`} data-testid={testid}>{value}</p>
    {delta && <p className={`text-xs mt-2 ${deltaTone === 'green' ? 'text-pos' : 'text-ink-soft'}`}>{delta}</p>}
  </div>
);

// List item: 34px circular outline icon + title/subtitle + right amount
export const ListItem = ({ icon: Icon, iconColor, title, subtitle, amount, sub, positive, onClick, testid, chevron }) => {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 bg-surface border border-line rounded-2xl px-4 py-3 text-left ${onClick ? 'active:scale-[0.98] transition-transform' : ''}`}
    >
      {Icon && (
        <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center shrink-0">
          <Icon strokeWidth={1.5} className="w-4 h-4" style={iconColor ? { color: iconColor } : undefined} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-ink truncate leading-tight">{title}</p>
        {subtitle && <p className="text-[11.5px] text-ink-soft truncate mt-0.5">{subtitle}</p>}
      </div>
      {amount != null && (
        <div className="text-right shrink-0">
          <p className={`text-sm font-semibold tabular ${positive ? 'text-pos' : 'text-ink'}`}>
            {positive ? '+' : ''}{amount}
          </p>
          {sub && <p className="text-[11px] text-ink-faint mt-0.5">{sub}</p>}
        </div>
      )}
      {chevron && <ChevronRight strokeWidth={1.5} className="w-4 h-4 text-ink-faint shrink-0" />}
    </Wrapper>
  );
};

export const Empty = ({ text }) => (
  <div className="text-center py-10 text-ink-faint text-sm">{text}</div>
);
